import React, { useMemo, useState } from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChartContainer,
  ChartEmptyState,
  ChartTooltip,
  InteractiveLegend,
  chartAnimation,
  getSeriesColor,
  useChartTheme,
} from './chartTheme';
import { colors, spacing, typography } from '../../design-system';

const PlayerRadarChart = ({
  players = [],
  metrics = [
    { key: 'pace', label: 'Velocidad' },
    { key: 'shooting', label: 'Remate' },
    { key: 'passing', label: 'Pase' },
    { key: 'dribbling', label: 'Regate' },
    { key: 'defending', label: 'Defensa' },
    { key: 'physical', label: 'Físico' },
  ],
  title = 'Comparación de Jugadores',
  height = 400,
  showLegend = true,
}) => {
  const chartTheme = useChartTheme(height);
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const limitedPlayers = players.slice(0, 3).map((player, index) => ({
    ...player,
    color: player.color || getSeriesColor(index),
  }));

  const radarData = useMemo(() => metrics.map((metric) => {
    const dataPoint = { metric: metric.label };
    limitedPlayers.forEach((player) => {
      dataPoint[player.name] = Number(player.stats?.[metric.key] || 0);
    });
    return dataPoint;
  }), [limitedPlayers, metrics]);

  const calculateOverall = (stats = {}) => {
    const values = Object.values(stats).map((value) => Number(value || 0));
    if (!values.length) {
      return 0;
    }
    return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1));
  };

  const toggleSeries = (dataKey) => {
    setHiddenKeys((current) => (
      current.includes(dataKey)
        ? current.filter((key) => key !== dataKey)
        : [...current, dataKey]
    ));
  };

  if (!limitedPlayers.length) {
    return (
      <ChartContainer title={title}>
        <ChartEmptyState message="No hay jugadores para comparar." />
      </ChartContainer>
    );
  }

  return (
    <ChartContainer title={title}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        {limitedPlayers.map((player) => {
          const overall = calculateOverall(player.stats);

          return (
            <Paper
              key={player.name}
              elevation={0}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: `${spacing.borderRadius.lg}px`,
                backgroundColor: alpha(player.color, 0.1),
                border: `1px solid ${alpha(player.color, 0.22)}`,
              }}
            >
              <Typography sx={{ color: 'text.primary', fontWeight: typography.fontWeight.semibold }}>
                {player.name}
              </Typography>
              <Typography sx={{ color: player.color, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, mt: 0.5 }}>
                {overall}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: typography.fontSize.xs }}>
                Rating global
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={radarData} outerRadius={chartTheme.isMobile ? '62%' : '72%'}>
          <PolarGrid stroke={chartTheme.palette.grid} />
          <PolarAngleAxis
            dataKey="metric"
            tick={{
              fill: chartTheme.palette.textMuted,
              fontSize: chartTheme.isMobile ? 10 : 12,
              fontFamily: chartTheme.fontFamily,
            }}
          />
          <PolarRadiusAxis
            domain={[0, 100]}
            tickCount={4}
            tick={chartTheme.isMobile ? false : {
              fill: chartTheme.palette.textMuted,
              fontSize: 10,
              fontFamily: chartTheme.fontFamily,
            }}
            axisLine={false}
          />
          <Tooltip content={<ChartTooltip valueFormatter={(value) => `${value}/100`} />} />

          {limitedPlayers
            .filter((player) => !hiddenKeys.includes(player.name))
            .map((player, index) => (
              <Radar
                key={player.name}
                name={player.name}
                dataKey={player.name}
                stroke={player.color}
                fill={player.color}
                fillOpacity={0.12}
                strokeWidth={2.5}
                dot={false}
                animationDuration={chartAnimation.duration}
                animationBegin={chartAnimation.begin + (index * chartAnimation.stagger)}
              />
            ))}
        </RadarChart>
      </ResponsiveContainer>

      {showLegend && (
        <InteractiveLegend
          items={limitedPlayers.map((player) => ({
            dataKey: player.name,
            name: player.name,
            color: player.color,
          }))}
          hiddenKeys={hiddenKeys}
          onToggle={toggleSeries}
          isMobile={chartTheme.isMobile}
        />
      )}

      <Stack spacing={1} sx={{ mt: 2 }}>
        {metrics.map((metric) => (
          <Box
            key={metric.key}
            sx={{
              display: 'grid',
              gridTemplateColumns: chartTheme.isMobile ? '1fr' : `120px repeat(${limitedPlayers.length}, 1fr)`,
              gap: 1,
              alignItems: 'center',
              p: 1,
              borderRadius: `${spacing.borderRadius.md}px`,
              backgroundColor: 'action.hover',
            }}
          >
            <Typography sx={{ color: 'text.secondary', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold }}>
              {metric.label}
            </Typography>
            {limitedPlayers.map((player) => (
              <Stack key={player.name} direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: player.color }} />
                <Typography sx={{ color: 'text.primary', fontSize: typography.fontSize.xs }}>
                  {player.stats?.[metric.key] || 0}
                </Typography>
              </Stack>
            ))}
          </Box>
        ))}
      </Stack>
    </ChartContainer>
  );
};

export default PlayerRadarChart;
